import React, { useMemo } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  Flex,
  Text,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { IPessoalEmpregado, IPessoalRescisao, IPessoalAfastamento } from '../../types';
import { UI } from '../../ui';
import { CollabAvatar, statusOf, CollabStatus } from './CollabAvatar';
import { classifyCargo, genderKey, prettyCargo } from './jobArchetypes';
import { anosDeCasa } from './CollabCard';

const ST_LABEL: Record<CollabStatus, string> = {
  ativo: 'Ativo',
  demitido: 'Demitido',
  afastado: 'Afastado',
  outro: 'Outro',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text
        fontSize="10px"
        fontWeight={800}
        color={UI.faint}
        textTransform="uppercase"
        letterSpacing="0.08em"
      >
        {label}
      </Text>
      <Text fontSize="14px" fontWeight={600} color={UI.ink}>
        {value}
      </Text>
    </Box>
  );
}

interface CollabDrawerProps {
  emp: IPessoalEmpregado | null;
  rescisoes: IPessoalRescisao[];
  afastamentos: IPessoalAfastamento[];
  isOpen: boolean;
  onClose: () => void;
}

export function CollabDrawer({ emp, rescisoes, afastamentos, isOpen, onClose }: CollabDrawerProps) {
  const arch = useMemo(() => (emp ? classifyCargo(emp.cargo) : null), [emp]);
  const meusAfast = useMemo(
    () => (emp ? afastamentos.filter(a => a.nome === emp.nome) : []),
    [emp, afastamentos],
  );
  const minhaResc = useMemo(
    () => (emp ? rescisoes.find(r => r.nome === emp.nome) : undefined),
    [emp, rescisoes],
  );

  if (!emp || !arch) return null;
  const st = statusOf(
    emp.status,
    meusAfast.some(a => !a.data_fim),
  );
  const anos = anosDeCasa(emp.admissao);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent bg={UI.bg ?? '#F6F8FB'}>
        <DrawerCloseButton mt={2} />
        <DrawerHeader
          borderBottom="1px solid"
          borderColor={UI.border}
          bgGradient={`linear(to-br, ${arch.gradient[0]}14, ${arch.gradient[1]}06)`}
        >
          <Flex align="center" gap={4}>
            <CollabAvatar
              gender={genderKey(emp.sexo)}
              archetype={arch}
              status={st}
              anos={anos}
              size={84}
            />
            <Box minW={0}>
              <Text fontSize="18px" fontWeight={800} color={UI.ink} noOfLines={2} lineHeight="1.2">
                {emp.nome}
              </Text>
              <Text fontSize="13px" color={UI.muted}>
                {prettyCargo(emp.cargo)}
              </Text>
              <Flex gap={1.5} mt={1.5} wrap="wrap">
                <Badge
                  style={{ background: `${arch.accent}1A`, color: arch.accent }}
                  borderRadius="full"
                  px={2}
                  fontSize="10.5px"
                  fontWeight={700}
                  textTransform="none"
                >
                  {arch.label}
                </Badge>
                <Badge
                  borderRadius="full"
                  px={2}
                  fontSize="10.5px"
                  fontWeight={700}
                  textTransform="none"
                >
                  {ST_LABEL[st]}
                </Badge>
              </Flex>
            </Box>
          </Flex>
        </DrawerHeader>

        <DrawerBody py={5}>
          <SimpleGrid columns={2} spacingY={4} spacingX={4}>
            <Field label="Departamento" value={emp.departamento || '—'} />
            <Field label="Vínculo" value={emp.vinculo || '—'} />
            <Field label="Salário" value={formatCurrency(emp.salario ?? 0)} />
            <Field label="Tempo de casa" value={anos > 0 ? `${anos} anos` : 'menos de 1 ano'} />
            <Field label="Admissão" value={emp.admissao || '—'} />
            <Field label="Gênero" value={emp.sexo || '—'} />
            <Field label="CPF" value={emp.cpf || '—'} />
            {emp.data_demissao && <Field label="Demissão" value={emp.data_demissao} />}
          </SimpleGrid>

          {minhaResc && (
            <Box mt={6} p={4} borderRadius="14px" bg={UI.negSoft} border={`1px solid ${UI.neg}33`}>
              <Text
                fontSize="12px"
                fontWeight={800}
                color={UI.neg}
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb={2}
              >
                Rescisão
              </Text>
              <SimpleGrid columns={2} spacingY={3} spacingX={4}>
                <Field label="Motivo" value={minhaResc.motivo} />
                <Field label="Dias trabalhados" value={String(minhaResc.dias)} />
                <Field label="Verbas" value={formatCurrency(minhaResc.proventos)} />
                {minhaResc.ma_contratacao && <Field label="Alerta" value="Saiu em < 90 dias" />}
              </SimpleGrid>
            </Box>
          )}

          {meusAfast.length > 0 && (
            <Box mt={6}>
              <Text
                fontSize="12px"
                fontWeight={800}
                color={UI.warn}
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb={2}
              >
                Afastamentos ({meusAfast.length})
              </Text>
              {meusAfast.map(a => (
                <Flex
                  key={`${a.tipo}-${a.data_inicio}`}
                  justify="space-between"
                  align="center"
                  p={3}
                  mb={2}
                  borderRadius="12px"
                  bg="white"
                  border={`1px solid ${UI.border}`}
                >
                  <Box>
                    <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                      {a.tipo}
                    </Text>
                    <Text fontSize="11.5px" color={UI.muted}>
                      {a.data_inicio} → {a.data_fim ?? 'em curso'}
                    </Text>
                  </Box>
                  <Text fontSize="13px" fontWeight={800} color={UI.warn}>
                    {a.dias} dias
                  </Text>
                </Flex>
              ))}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
